import { useCallback, useMemo } from 'react';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { z } from 'zod';

import { codec, useQueryParams } from '@/hooks/router/use-query-params';
import { useMe } from '@/hooks/use-me';
import { useNow } from '@/hooks/useNow';

import type { Domain } from '../components/filters/domain-toggle';
import { ChartType, ChartTypeSchema } from '../entities/chart-type';
import type { Filters } from '../entities/filters';

export function useFilters() {
  const me = useMe();
  const {
    config: { defaultDataExplorerFilters: defaultFilters },
  } = me;
  const now = useNow();

  const [queryState, queryParams] = useQueryParams({
    deviceId: {
      key: 'device',
      schema: z.string().optional(),
      codec: codec.string,
    },
    domain: {
      key: 'domain',
      schema: z.enum(['battery', 'pcs']).optional(),
      codec: codec.string,
    },
    chartTypes: {
      key: 'chart',
      schema: z.array(ChartTypeSchema).optional(),
      codec: codec.array(codec.string, '|'),
    },
    from: {
      key: 'from',
      schema: z.date().optional(),
      codec: codec.date,
    },
    to: {
      key: 'to',
      schema: z.date().optional(),
      codec: codec.date,
    },
    filterNoise: {
      key: 'filter_noise',
      schema: z.boolean().optional(),
      codec: codec.boolean,
    },
    arrayId: {
      key: 'array_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
    nodeId: {
      key: 'node_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
    containerId: {
      key: 'container_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
    bankId: {
      key: 'bank_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
    packId: {
      key: 'pack_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
    moduleId: {
      key: 'module_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
    cellId: {
      key: 'cell_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
    pcsId: {
      key: 'pcs_id',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '|'),
    },
  });

  const {
    deviceId,
    domain: rawDomain = 'battery',
    chartTypes = Object.values(ChartType),
    from = addDays(now, -defaultFilters.timeRange),
    to = now,
    filterNoise = true,
    arrayId,
    nodeId,
    containerId,
    bankId,
    packId,
    moduleId,
    cellId,
    pcsId,
  } = queryState;

  // Force battery domain for non-BESS customers
  const isBess = me.config.type === 'bess';
  const domain = isBess ? rawDomain : 'battery';

  // Helper function to normalize arrays - single value for single element arrays, array for multiple
  const normalizeArrayValue = (
    value: string[] | undefined,
  ): string | string[] | undefined => {
    if (!value || value.length === 0) return undefined;
    if (value.length === 1) return value[0];
    return value;
  };

  const filters: Filters = useMemo(
    () => ({
      coreFilters: {
        chartTypes,
        from: startOfDay(from),
        to: endOfDay(to),
        filterNoise,
      },
      batteryFilters: {
        deviceId,
        arrayId: normalizeArrayValue(arrayId),
        nodeId: normalizeArrayValue(nodeId),
        containerId: normalizeArrayValue(containerId),
        bankId: normalizeArrayValue(bankId),
        packId: normalizeArrayValue(packId),
        moduleId: normalizeArrayValue(moduleId),
        cellId: normalizeArrayValue(cellId),
      },
      pcsFilters: {
        deviceId,
        pcsId: normalizeArrayValue(pcsId),
      },
    }),
    [
      deviceId,
      chartTypes,
      from,
      to,
      filterNoise,
      arrayId,
      nodeId,
      containerId,
      bankId,
      packId,
      moduleId,
      cellId,
      pcsId,
    ],
  );

  const updateFilters = useCallback(
    (partial: Partial<Filters>) => {
      const params: Record<string, any> = { domain }; // preserve the current domain

      // Flatten battery filters
      if (partial.batteryFilters) {
        Object.entries(partial.batteryFilters).forEach(([key, value]) => {
          // Convert single values to arrays for URL encoding
          if (value !== undefined) {
            params[key] = Array.isArray(value) ? value : [value];
          } else {
            params[key] = undefined;
          }
        });
      }

      // Flatten pcs filters
      if (partial.pcsFilters) {
        // For PCS domain, map ANY level ID to pcsId in URL params
        // Some customers have PCS hierarchies that use battery level names (e.g., "array")
        // but they should all be stored as pcsId in the URL
        let foundPcsValue: string | string[] | undefined = undefined;

        Object.entries(partial.pcsFilters).forEach(([key, value]) => {
          if (key !== 'deviceId' && value !== undefined) {
            // Found a PCS level ID (could be pcsId, arrayId, etc.)
            foundPcsValue = value;
          }
        });

        // Also check explicit pcsId
        if (partial.pcsFilters.pcsId !== undefined) {
          foundPcsValue = partial.pcsFilters.pcsId;
        }

        if (foundPcsValue !== undefined) {
          params.pcsId = Array.isArray(foundPcsValue)
            ? foundPcsValue
            : [foundPcsValue];
        } else {
          params.pcsId = undefined;
        }
      }

      // Flatten coreFilters
      if (partial.coreFilters) {
        const { chartTypes, from, to, filterNoise } = partial.coreFilters;
        if (chartTypes !== undefined) params.chartTypes = chartTypes;
        if (from !== undefined) params.from = from;
        if (to !== undefined) params.to = to;
        if (filterNoise !== undefined) params.filterNoise = filterNoise;
      }

      // Clear any lower-level battery IDs if a higher one changed
      const HIERARCHY = [
        'array',
        'node',
        'container',
        'bank',
        'pack',
        'module',
        'cell',
      ] as const;
      const changed = Object.keys(params).find(
        (k) =>
          k.endsWith('Id') &&
          HIERARCHY.includes(k.replace('Id', '') as (typeof HIERARCHY)[number]),
      );
      if (changed) {
        const idx = HIERARCHY.indexOf(
          changed.replace('Id', '') as (typeof HIERARCHY)[number],
        );
        for (let i = idx + 1; i < HIERARCHY.length; i++) {
          params[`${HIERARCHY[i]}Id`] = undefined;
        }
      }

      queryParams.replace(params);
    },
    [queryParams, domain],
  );

  const updateDomain = useCallback(
    (newDomain: Domain) => {
      queryParams.replace({ domain: newDomain });
    },
    [queryParams],
  );

  return {
    filters,
    domain,
    updateFilters,
    updateDomain,
  };
}
